import { type ComponentPropsWithoutRef, memo } from 'react';
import { useDrop } from 'react-dnd';

import { cn } from '@air/utils-tailwind';

import { boardDragTypes, type DropData } from '~/components/Shared/Drag/dragTypes';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type DNDBoardsRootProps = Pick<ComponentPropsWithoutRef<'div'>, 'children' | 'className'>;

export const DNDBoardsRoot = memo(({ className, children }: DNDBoardsRootProps) => {
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: boardDragTypes,
    drop: (): DropData => ({
      type: 'move',
      location: 'sub-nav-rearrange',
    }),
    collect: (monitor) => {
      const item = monitor.getItem();
      /**
       * Checks if the board that is being dropped is at the root level.
       */
      const isNotRootBoard = item?.firstItem?.library?.id || item?.firstItem?.ancestors?.length !== 0;

      return {
        isOver: monitor.isOver(),
        canDrop: canCreateRootBoards && isNotRootBoard,
      };
    },
  });

  return (
    <div
      className={cn('group/droppable-item relative h-full', isOver && canDrop ? 'hovering-drop' : undefined, className)}
      ref={drop}
    >
      {children}
    </div>
  );
});

DNDBoardsRoot.displayName = 'DNDBoardsRoot';
